import { useEffect, useState } from "react";
import type { Deal } from "../types";
import { simulateGrowth, solveGoal, type DealTemplate, type GoalLever, type GoalResult } from "../lib/growth";
import { listScenarios, saveScenario, deleteScenario, newScenarioId, type Scenario } from "../lib/scenarios";
import { HORIZONS } from "../lib/projection";
import { money0, pct, x2 } from "../lib/format";
import { LineChart, type Series } from "./Charts";

const COMPARE_COLORS = ["#8C6A34", "#5E8776", "#A6503C", "#2C5C4A"];
const LEVER_LABEL: Record<GoalLever, string> = { valueAddPct: "value-add %", maxPerYear: "buys per year", startCapital: "starting cash" };
const fmtLever = (l: GoalLever, v: number) => (l === "valueAddPct" ? pct(v, 1) : l === "startCapital" ? money0(v) : String(v));

const C = { net: "#17352B", existing: "#5E8776", props: "#B08948", cash: "#2C5C4A" };

function Kpi({ label, value, sub, good }: { label: string; value: string; sub?: string; good?: boolean }) {
  return (
    <div className="card kpi">
      <div className="kl">{label}</div>
      <div className="kv">{value}</div>
      {sub && <div className={"ks" + (good ? " good" : "")}>{sub}</div>}
    </div>
  );
}
function Num({ label, v, set, step = 1, suffix }: { label: string; v: number; set: (n: number) => void; step?: number; suffix?: string }) {
  return (
    <div className="fld">
      <label>{label}</label>
      <input type="number" step={step} value={v} onChange={(e) => set(parseFloat(e.target.value) || 0)} />
      {suffix && <div style={{ fontSize: 10, color: "var(--muted)" }}>{suffix}</div>}
    </div>
  );
}

export function Growth({ deals: allDeals }: { deals: Deal[] }) {
  const deals = allDeals.filter((d) => d.kind !== "land-development");
  const avg = (f: (d: Deal) => number) => (deals.length ? deals.reduce((s, d) => s + f(d), 0) / deals.length : 0);
  const [t, setT] = useState<DealTemplate>({
    price: Math.round((avg((d) => d.purchase) || 500000) / 1000) * 1000,
    downPct: 0.25, capRate: 0.07,
    growth: +(avg((d) => d.rentGrow) || 0.03).toFixed(3),
    valueAddPct: 0.05,
    rate: +(avg((d) => d.permRate) || 0.0625).toFixed(4),
    amort: 30, refiLTV: +(avg((d) => d.targetLTV) || 0.75).toFixed(2), refiAge: 5,
    startCapital: 150000, maxPerYear: 2,
  });
  const [horizon, setHorizon] = useState<number>(20);
  const up = (patch: Partial<DealTemplate>) => setT((s) => ({ ...s, ...patch }));

  // saved scenarios (name & compare)
  const [saved, setSaved] = useState<Scenario[]>([]);
  const [name, setName] = useState("");
  const [compareOn, setCompareOn] = useState(false);
  useEffect(() => { listScenarios().then(setSaved); }, []);
  const doSave = async () => {
    if (!name.trim()) return;
    const s: Scenario = { id: newScenarioId(), name: name.trim(), template: t, horizon };
    await saveScenario(s);
    setSaved(await listScenarios());
    setName("");
    setCompareOn(true);
  };
  const doDelete = async (id: string) => { await deleteScenario(id); setSaved(await listScenarios()); };
  const doLoad = (s: Scenario) => { setT(s.template); setHorizon(s.horizon); };

  // goal seek
  const [goalMonthly, setGoalMonthly] = useState(15000);
  const [goalYear, setGoalYear] = useState(15);
  const [goalLever, setGoalLever] = useState<GoalLever>("maxPerYear");
  const [goal, setGoal] = useState<GoalResult | null>(null);
  const runGoal = () => setGoal(solveGoal(deals, t, horizon, goalMonthly, goalYear, goalLever));

  const r = simulateGrowth(deals, t, horizon);
  const years = r.years.map((y) => y.year);
  const start = r.years[0];
  const end = r.end;
  const multiple = end.netWorth / Math.max(1, start.netWorth);
  const buys = r.years.filter((y) => y.bought > 0);

  const netSeries: Series[] = [{ name: "This plan", color: C.net, values: r.years.map((y) => y.netWorth), fill: true }];
  if (compareOn && saved.length) {
    saved.slice(0, 4).forEach((sc, i) => {
      const rr = simulateGrowth(deals, sc.template, horizon);
      netSeries.push({ name: sc.name, color: COMPARE_COLORS[i % 4], values: rr.years.map((y) => y.netWorth), dashed: true });
    });
  } else {
    netSeries.push({ name: "Existing properties only", color: C.existing, values: r.years.map((y) => y.existingEquity), dashed: true });
  }

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ The snowball — one property to many</div>
        <p>
          Start with your <b>{deals.length}</b> {deals.length === 1 ? "property" : "properties"} and reinvest the cash: every
          year, rent flow and <b>refinance proceeds</b> pool up, and when there's enough for a down payment you buy the next
          one. Each new building then pays down, appreciates, and refinances to fund the one after it. Adjust the deal you'd
          repeat and watch it compound.
        </p>
      </div>

      {/* controls */}
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Horizon</div>
            <div className="depthbar" style={{ border: "1px solid var(--line)", borderRadius: 100 }}>
              {HORIZONS.map((h) => (
                <button key={h} className={horizon === h ? "on" : ""} style={{ color: horizon === h ? "#241a08" : "var(--forest)", padding: "6px 16px" }} onClick={() => setHorizon(h)}>{h} yr</button>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Forced value-add per deal <span className="mono" style={{ color: "var(--brass-deep)" }}>+{pct(t.valueAddPct, 0)} value</span></div>
            <input type="range" min={0} max={0.15} step={0.01} value={t.valueAddPct} onChange={(e) => up({ valueAddPct: +e.target.value })} style={{ width: "100%", accentColor: "var(--brass)" }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Starting cash to deploy <span className="mono" style={{ color: "var(--brass-deep)" }}>{money0(t.startCapital)}</span></div>
            <input type="range" min={0} max={500000} step={10000} value={t.startCapital} onChange={(e) => up({ startCapital: +e.target.value })} style={{ width: "100%", accentColor: "var(--brass)" }} />
          </div>
        </div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>The deal you'd repeat</div>
        <div className="inputs">
          <Num label="Price" v={t.price} set={(n) => up({ price: n })} step={10000} suffix={money0(t.price)} />
          <Num label="Down %" v={Math.round(t.downPct * 100)} set={(n) => up({ downPct: n / 100 })} suffix={money0(t.price * t.downPct) + " down"} />
          <Num label="Cap rate %" v={+(t.capRate * 100).toFixed(1)} set={(n) => up({ capRate: n / 100 })} step={0.1} />
          <Num label="Growth %/yr" v={+(t.growth * 100).toFixed(1)} set={(n) => up({ growth: n / 100 })} step={0.1} />
          <Num label="Rate %" v={+(t.rate * 100).toFixed(2)} set={(n) => up({ rate: n / 100 })} step={0.05} />
          <Num label="Refi LTV %" v={Math.round(t.refiLTV * 100)} set={(n) => up({ refiLTV: n / 100 })} />
          <Num label="Refi after (yr)" v={t.refiAge} set={(n) => up({ refiAge: Math.max(1, Math.round(n)) })} />
          <Num label="Max buys/yr" v={t.maxPerYear} set={(n) => up({ maxPerYear: Math.max(1, Math.round(n)) })} />
        </div>
      </div>

      {/* headline */}
      <div className="grid g4">
        <Kpi label={`Properties in ${horizon} yr`} value={String(end.totalProps)} sub={"+" + end.newProps + " bought"} good={end.newProps > 0} />
        <Kpi label="Net worth" value={money0(end.netWorth)} sub={"from " + money0(start.netWorth) + " today"} good />
        <Kpi label="Net-worth multiple" value={x2(multiple)} sub={`over ${horizon} years`} good />
        <Kpi label="Cash flow / yr" value={money0(end.cashFlow)} sub={money0(end.cashFlow / 12) + "/mo"} good />
      </div>

      {/* goal seek + save/compare */}
      <div className="grid g2" style={{ alignItems: "start" }}>
        <div className="card pad">
          <div className="eyebrow">🎯 Goal — reach a passive-income target</div>
          <div className="inputs" style={{ marginTop: 8 }}>
            <div className="fld"><label>Target income /mo</label><input type="number" step={500} value={goalMonthly} onChange={(e) => setGoalMonthly(+e.target.value || 0)} /></div>
            <div className="fld"><label>By year</label><input type="number" step={1} value={goalYear} onChange={(e) => setGoalYear(Math.max(1, Math.round(+e.target.value) || 1))} /></div>
            <div className="fld"><label>Solve for</label>
              <select value={goalLever} onChange={(e) => setGoalLever(e.target.value as GoalLever)}>
                <option value="maxPerYear">buys per year</option>
                <option value="valueAddPct">value-add %</option>
                <option value="startCapital">starting cash</option>
              </select>
            </div>
          </div>
          <button className="btn btn-p btn-sm" style={{ marginTop: 10 }} onClick={runGoal}>Find the plan</button>
          {goal && (
            <div style={{ marginTop: 10 }}>
              <div className="flag ok"><span>◆</span><div>
                Current plan: {goal.currentHitYear !== null
                  ? <>reaches <b>{money0(goal.targetAnnual / 12)}/mo</b> in <b>year {goal.currentHitYear}</b> (at year {goal.byYear} it's {money0(goal.currentAtGoalYear / 12)}/mo).</>
                  : <>doesn't reach {money0(goal.targetAnnual / 12)}/mo within {horizon} years (year {goal.byYear}: {money0(goal.currentAtGoalYear / 12)}/mo).</>}
              </div></div>
              <div className={"flag " + (goal.achievable ? "ok" : "warn")} style={{ marginTop: 6 }}>
                <span>{goal.achievable ? "✓" : "◐"}</span>
                <div>{goal.achievable
                  ? <>To hit <b>{money0(goal.targetAnnual / 12)}/mo by year {goal.byYear}</b>, set <b>{LEVER_LABEL[goal.lever]}</b> to <b>{fmtLever(goal.lever, goal.neededValue)}</b>.{" "}
                      <a style={{ cursor: "pointer", color: "var(--brass-deep)" }} onClick={() => up({ [goal.lever]: goal.neededValue } as Partial<DealTemplate>)}>Apply this →</a></>
                  : <>Even at the max {LEVER_LABEL[goal.lever]}, you fall short by year {goal.byYear}. Extend the horizon or combine levers.</>}</div>
              </div>
            </div>
          )}
        </div>

        <div className="card pad">
          <div className="eyebrow">💾 Save &amp; compare scenarios</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <input placeholder="name this scenario…" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, minWidth: 160, padding: "7px 10px", border: "1px solid var(--line)", borderRadius: 7, fontSize: 13, background: "var(--white)" }} />
            <button className="btn btn-a btn-sm" onClick={doSave} disabled={!name.trim()}>Save</button>
          </div>
          {saved.length > 0 && (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--forest)", margin: "10px 0 4px" }}>
                <input type="checkbox" checked={compareOn} onChange={(e) => setCompareOn(e.target.checked)} /> Overlay saved plans on the chart
              </label>
              <table className="t" style={{ marginTop: 4 }}>
                <tbody>
                  {saved.map((s, i) => (
                    <tr key={s.id}>
                      <td>{compareOn && i < 4 && <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: COMPARE_COLORS[i % 4], marginRight: 7 }} />}{s.name}</td>
                      <td className="n" style={{ whiteSpace: "nowrap" }}>
                        <a style={{ cursor: "pointer", color: "var(--brass-deep)" }} onClick={() => doLoad(s)}>load</a>
                        <a style={{ cursor: "pointer", color: "var(--fail)", marginLeft: 10 }} onClick={() => doDelete(s.id)}>delete</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {saved.length === 0 && <div className="note">Save the current levers as a named plan, then compare plans side-by-side on the chart.</div>}
        </div>
      </div>

      {/* net worth */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Net worth — reinvesting the snowball</h2>
      <div className="card pad">
        <LineChart years={years} series={netSeries} height={280} />
        <div className="note">{compareOn && saved.length ? <>Comparing <b>{Math.min(4, saved.length)}</b> saved plan{saved.length > 1 ? "s" : ""} against this one.</> : <>The gap between the lines is wealth from <b>new acquisitions</b> — {money0(end.netWorth - end.existingEquity)} built by reinvesting, on top of your original book.</>}</div>
      </div>

      {/* property count + cash flow */}
      <div className="grid g2" style={{ alignItems: "start" }}>
        <div className="card pad">
          <div className="eyebrow">Properties owned over time</div>
          <LineChart years={years} series={[{ name: "Properties", color: C.props, values: r.years.map((y) => y.totalProps), fill: true }]} height={210} />
        </div>
        <div className="card pad">
          <div className="eyebrow">Annual cash flow over time</div>
          <LineChart years={years} series={[{ name: "Cash flow / yr", color: C.cash, values: r.years.map((y) => y.cashFlow), fill: true }]} height={210} />
        </div>
      </div>

      {/* acquisitions timeline */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Acquisition timeline</h2>
      <div className="card pad">
        {buys.length === 0 ? (
          <div className="note">No acquisitions in this scenario — increase starting cash, lower the price, or raise the value-add to fund the first purchase.</div>
        ) : (
          <div className="tablewrap">
            <table className="t">
              <thead><tr><th>Year</th><th className="n">Bought</th><th className="n">Total properties</th><th className="n">Net worth</th><th className="n">Cash flow/yr</th></tr></thead>
              <tbody>
                {buys.map((y) => (
                  <tr key={y.year}>
                    <td><b>Year {y.year}</b></td>
                    <td className="n">+{y.bought}</td>
                    <td className="n">{y.totalProps}</td>
                    <td className="n">{money0(y.netWorth)}</td>
                    <td className="n">{money0(y.cashFlow)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="note" style={{ marginTop: 8 }}>Illustrative model — each acquisition assumes the repeatable deal above. Refis pull {pct(t.refiLTV, 0)} LTV at year {t.refiAge}; discipline caps you at {t.maxPerYear} buy{t.maxPerYear > 1 ? "s" : ""}/yr.</div>
      </div>
    </div>
  );
}
